/**
 * ESS POS — Station Terminal Activation & Fast Staff PIN Login
 *
 * Operational POS Design:
 * 1. Station Activation & Pairing (One-time setup per POS device / browser)
 *    - Branch Code (e.g. EDA001A, MAIN) + Station PIN (e.g. 12345654321)
 *    - Persisted in localStorage ('ess_pos_paired_station'). Never asks again!
 * 2. Fast Staff PIN Pad:
 *    - Waiters, Cashiers, Managers & Storekeepers select their name and enter a 4-digit PIN.
 *    - Sub-second login directly into their role-specific workspace.
 * 3. Discrete Super Admin Login:
 *    - Subtle small platform admin access button at the bottom for Abdul Bilal Manda.
 */

import { apiClient } from '../api/apiClient';
import { escapeHtml, showToast } from '../components/ui';

interface PairedStationData {
  tenant: {
    id: string;
    name: string;
    slug: string;
    currency: string;
    status: string;
  };
  branch: {
    id: string;
    name: string;
    code: string;
  };
  staff: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
    primaryRole: string;
    hasPin?: boolean;
  }>;
}

const STATION_STORAGE_KEY = 'ess_pos_paired_station';

export function renderAuthScreen(onAuthenticated: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'ess-station-wrapper';

  let currentView: 'PAIRED_PIN' | 'PAIR_STATION' | 'SUPER_ADMIN' = 'PAIR_STATION';
  let pairedData: PairedStationData | null = null;
  let selectedStaff: any = null;
  let currentPin = '';
  let isAuthenticating = false;

  // Check if terminal is already paired
  try {
    const raw = localStorage.getItem(STATION_STORAGE_KEY);
    if (raw) {
      pairedData = JSON.parse(raw);
      if (pairedData && pairedData.tenant && pairedData.branch) {
        currentView = 'PAIRED_PIN';
        if (pairedData.staff && pairedData.staff.length > 0) {
          selectedStaff = pairedData.staff[0];
        }
      }
    }
  } catch {
    localStorage.removeItem(STATION_STORAGE_KEY);
  }

  // Helper: Format role badge label
  const formatRoleName = (role: string): string => {
    switch (role) {
      case 'CASHIER': return 'Cashier';
      case 'WAITER': return 'Waiter';
      case 'MANAGER': return 'Manager';
      case 'INVENTORY_CLERK': return 'Stock Keeper';
      case 'OWNER': return 'Restaurant Owner';
      default: return role;
    }
  };

  const formatRoleColor = (role: string): string => {
    switch (role) {
      case 'CASHIER': return 'var(--ess-success-text)';
      case 'WAITER': return '#0284C7';
      case 'MANAGER': return '#7C3AED';
      case 'INVENTORY_CLERK': return 'var(--ess-primary)';
      case 'OWNER': return '#D97706';
      default: return 'var(--ess-text-secondary)';
    }
  };

  // Keyboard handler for fast numeric PIN typing
  const handleKeydown = (e: KeyboardEvent) => {
    if (currentView !== 'PAIRED_PIN' || isAuthenticating || !selectedStaff) return;

    if (e.key >= '0' && e.key <= '9') {
      if (currentPin.length < 4) {
        currentPin += e.key;
        updatePinDisplay();
        if (currentPin.length === 4) {
          submitPin();
        }
      }
    } else if (e.key === 'Backspace') {
      if (currentPin.length > 0) {
        currentPin = currentPin.slice(0, -1);
        updatePinDisplay();
      }
    } else if (e.key === 'Enter') {
      if (currentPin.length >= 4) {
        submitPin();
      }
    }
  };

  window.addEventListener('keydown', handleKeydown);

  const cleanupListeners = () => {
    window.removeEventListener('keydown', handleKeydown);
  };

  const updatePinDisplay = (isError = false) => {
    const dots = container.querySelectorAll('.ess-pin-dot');
    dots.forEach((dot, idx) => {
      dot.classList.remove('filled', 'error');
      if (isError) {
        dot.classList.add('error');
      } else if (idx < currentPin.length) {
        dot.classList.add('filled');
      }
    });
  };

  const submitPin = async () => {
    if (!pairedData || !selectedStaff || isAuthenticating) return;
    if (currentPin.length < 4) {
      showToast({ title: 'PIN Required', message: 'Please enter your 4-digit PIN', type: 'warning' });
      return;
    }

    isAuthenticating = true;
    const errorBox = container.querySelector('#ess-pin-error') as HTMLElement | null;
    if (errorBox) errorBox.style.display = 'none';

    try {
      await apiClient.staffPinLogin({
        tenantId: pairedData.tenant.id,
        userId: selectedStaff.id,
        pin: currentPin,
      });

      cleanupListeners();
      showToast({
        title: `Welcome, ${selectedStaff.firstName}!`,
        message: `Authorized ${formatRoleName(selectedStaff.primaryRole)} Workspace`,
        type: 'success',
      });
      onAuthenticated();
    } catch (err: any) {
      isAuthenticating = false;
      updatePinDisplay(true);
      if (errorBox) {
        errorBox.textContent = err?.message || 'Incorrect PIN entered. Please try again.';
        errorBox.style.display = 'block';
      }
      setTimeout(() => {
        currentPin = '';
        updatePinDisplay(false);
      }, 700);
    }
  };

  const render = () => {
    if (currentView === 'PAIRED_PIN' && pairedData) {
      container.innerHTML = `
        <!-- Top Station Identity Bar -->
        <header class="ess-station-topbar">
          <div class="ess-station-identity">
            <div class="ess-brand-mark" style="width:32px;height:32px;font-size:13px;border-radius:6px;">ESS</div>
            <div class="ess-station-name">
              ${escapeHtml(pairedData.tenant.name)}
              <span class="ess-station-badge">Branch: ${escapeHtml(pairedData.branch.code || 'MAIN')}</span>
            </div>
          </div>
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="ess-unlink-station" title="Switch or re-pair station branch">
            Change Station
          </button>
        </header>

        <!-- Main Staff Selection & PIN Pad Body -->
        <main class="ess-station-body">
          <div class="ess-station-card">
            <div class="ess-station-grid">
              
              <!-- Left Column: Staff Directory -->
              <div class="ess-staff-section">
                <div class="ess-staff-section-title">Select Staff Member</div>
                <div class="ess-staff-section-sub">Choose your name to unlock the POS workspace</div>

                <div class="ess-staff-list" id="ess-staff-list">
                  ${
                    pairedData.staff && pairedData.staff.length > 0
                      ? pairedData.staff
                          .map((s) => {
                            const isSel = selectedStaff && selectedStaff.id === s.id;
                            const initials = (s.firstName[0] || '') + (s.lastName[0] || '');
                            return `
                        <div class="ess-staff-card ${isSel ? 'selected' : ''}" data-staff-id="${escapeHtml(s.id)}">
                          <div class="ess-staff-avatar">${escapeHtml(initials.toUpperCase())}</div>
                          <div class="ess-staff-info">
                            <div class="ess-staff-name">${escapeHtml(s.firstName)} ${escapeHtml(s.lastName)}</div>
                            <div class="ess-staff-role" style="color: ${formatRoleColor(s.primaryRole)}">
                              &bull; ${formatRoleName(s.primaryRole)}
                            </div>
                          </div>
                          ${isSel ? '<span style="color:var(--ess-primary);font-size:16px;font-weight:bold;">&check;</span>' : ''}
                        </div>
                      `;
                          })
                          .join('')
                      : '<div style="padding:16px;color:var(--ess-text-muted);font-size:13px;">No staff accounts found. Please add staff in the Manager workspace.</div>'
                  }
                </div>
              </div>

              <!-- Right Column: Fast 4-Digit PIN Pad -->
              <div class="ess-pin-section">
                <div class="ess-pin-target-label">Signing in as</div>
                <div class="ess-pin-target-name" id="ess-pin-staff-name">
                  ${selectedStaff ? `${escapeHtml(selectedStaff.firstName)} ${escapeHtml(selectedStaff.lastName)}` : 'Select a Staff Profile'}
                </div>

                <div id="ess-pin-error" class="ess-form-error" style="display:none;margin-bottom:12px;width:100%;max-width:260px;" role="alert"></div>

                <!-- 4 PIN Dots -->
                <div class="ess-pin-dots">
                  <span class="ess-pin-dot"></span>
                  <span class="ess-pin-dot"></span>
                  <span class="ess-pin-dot"></span>
                  <span class="ess-pin-dot"></span>
                </div>

                <!-- Numeric Touch Keypad -->
                <div class="ess-pin-keypad">
                  <button type="button" class="ess-pin-key" data-key="1">1</button>
                  <button type="button" class="ess-pin-key" data-key="2">2</button>
                  <button type="button" class="ess-pin-key" data-key="3">3</button>
                  <button type="button" class="ess-pin-key" data-key="4">4</button>
                  <button type="button" class="ess-pin-key" data-key="5">5</button>
                  <button type="button" class="ess-pin-key" data-key="6">6</button>
                  <button type="button" class="ess-pin-key" data-key="7">7</button>
                  <button type="button" class="ess-pin-key" data-key="8">8</button>
                  <button type="button" class="ess-pin-key" data-key="9">9</button>
                  <button type="button" class="ess-pin-key ess-pin-key-action" data-key="backspace" title="Backspace">&larr;</button>
                  <button type="button" class="ess-pin-key" data-key="0">0</button>
                  <button type="button" class="ess-pin-key ess-pin-key-enter" data-key="enter" title="Sign In">&rarr;</button>
                </div>
              </div>

            </div>

            <!-- Footer Bar with Discrete Super Admin Link -->
            <footer class="ess-station-footer">
              <span>ESS POS Station &bull; Easy Smart System</span>
              <button type="button" class="ess-discrete-superadmin-btn" id="ess-goto-superadmin">
                🔐 Super Admin Login
              </button>
            </footer>
          </div>
        </main>
      `;

      // Background fresh staff refresh
      apiClient.stationStaffList(pairedData.tenant.id, pairedData.branch.id).then((freshStaff) => {
        if (freshStaff && freshStaff.length > 0 && pairedData) {
          pairedData.staff = freshStaff;
          localStorage.setItem(STATION_STORAGE_KEY, JSON.stringify(pairedData));
        }
      }).catch(() => {});

      // Bind Staff Card Selection
      container.querySelectorAll('.ess-staff-card').forEach((card) => {
        card.addEventListener('click', () => {
          const staffId = card.getAttribute('data-staff-id');
          selectedStaff = pairedData?.staff.find((s) => s.id === staffId) || null;
          currentPin = '';
          render();
        });
      });

      // Bind Numeric Keypad Buttons
      container.querySelectorAll('.ess-pin-key').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.getAttribute('data-key');
          if (isAuthenticating || !selectedStaff) return;

          if (key === 'backspace') {
            if (currentPin.length > 0) {
              currentPin = currentPin.slice(0, -1);
              updatePinDisplay();
            }
          } else if (key === 'enter') {
            if (currentPin.length >= 4) {
              submitPin();
            }
          } else if (key && currentPin.length < 4) {
            currentPin += key;
            updatePinDisplay();
            if (currentPin.length === 4) {
              submitPin();
            }
          }
        });
      });

      // Bind Unlink / Change Station
      container.querySelector('#ess-unlink-station')?.addEventListener('click', () => {
        if (confirm(`Unlink this POS station from ${pairedData?.tenant.name}? You will need to re-enter your branch code.`)) {
          localStorage.removeItem(STATION_STORAGE_KEY);
          pairedData = null;
          currentPin = '';
          currentView = 'PAIR_STATION';
          render();
        }
      });

      // Bind Discrete Super Admin link
      container.querySelector('#ess-goto-superadmin')?.addEventListener('click', () => {
        currentView = 'SUPER_ADMIN';
        render();
      });

      updatePinDisplay();

    } else if (currentView === 'PAIR_STATION') {
      // Unpaired Terminal View
      container.innerHTML = `
        <main class="ess-station-body" style="padding:40px 16px;">
          <div class="ess-pairing-box">
            <div class="ess-pairing-header">
              <div class="ess-brand-mark ess-brand-mark-lg" style="margin:0 auto 12px auto;">ESS</div>
              <div class="ess-pairing-title">POS Station Activation</div>
              <div class="ess-pairing-desc">
                Pair this device once to your branch. Staff will log in directly using their personal PIN without re-entering branch credentials.
              </div>
            </div>

            <div id="ess-pairing-error" class="ess-form-error" style="display:none;margin-bottom:14px;" role="alert"></div>

            <form id="ess-pairing-form" class="ess-form" novalidate>
              <div class="ess-form-group">
                <label class="ess-label" for="pair-branch-code">Branch Identity / Code *</label>
                <input
                  type="text"
                  id="pair-branch-code"
                  class="ess-input"
                  placeholder="e.g. sip-chat-cafe, swahili-plate, or MAIN"
                  value=""
                  required
                  autocomplete="off"
                />
                <span class="ess-field-hint">Enter your restaurant slug (e.g. <code>sip-chat-cafe</code>) or branch code.</span>
              </div>

              <div class="ess-form-group">
                <label class="ess-label" for="pair-station-pin">Station Terminal PIN</label>
                <input
                  type="password"
                  id="pair-station-pin"
                  class="ess-input"
                  placeholder="Default: 12345654321"
                  value="12345654321"
                  autocomplete="off"
                />
              </div>

              <button type="submit" class="ess-btn ess-btn-primary ess-btn-lg ess-btn-block" id="ess-pair-btn" style="margin-top:8px;">
                Link &amp; Activate POS Terminal
              </button>
            </form>

            <div style="margin-top:24px;text-align:center;border-top:1px solid var(--ess-border);padding-top:14px;">
              <button type="button" class="ess-discrete-superadmin-btn" id="ess-pairing-superadmin">
                🔐 Super Admin Login
              </button>
            </div>
          </div>
        </main>
      `;

      const pairForm = container.querySelector('#ess-pairing-form') as HTMLFormElement | null;
      const pairError = container.querySelector('#ess-pairing-error') as HTMLElement | null;

      pairForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const branchCode = (container.querySelector('#pair-branch-code') as HTMLInputElement).value.trim();
        const stationPin = (container.querySelector('#pair-station-pin') as HTMLInputElement).value.trim();

        if (!branchCode) {
          if (pairError) {
            pairError.textContent = 'Please enter your branch code or identifier.';
            pairError.style.display = 'block';
          }
          return;
        }

        const submitBtn = container.querySelector('#ess-pair-btn') as HTMLButtonElement;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Linking Station...';
        if (pairError) pairError.style.display = 'none';

        try {
          const res = await apiClient.stationPair({
            branchCode,
            stationPin: stationPin || undefined,
          });

          pairedData = {
            tenant: res.tenant,
            branch: res.branch,
            staff: res.staff,
          };
          localStorage.setItem(STATION_STORAGE_KEY, JSON.stringify(pairedData));

          selectedStaff = pairedData.staff.length > 0 ? pairedData.staff[0] : null;
          currentPin = '';
          currentView = 'PAIRED_PIN';
          showToast({
            title: 'Station Activated',
            message: `Linked to ${res.tenant.name} (${res.branch.code || 'MAIN'})`,
            type: 'success',
          });
          render();
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Link & Activate POS Terminal';
          if (pairError) {
            pairError.textContent = err?.message || 'Failed to activate terminal. Please check branch code.';
            pairError.style.display = 'block';
          }
        }
      });

      container.querySelector('#ess-pairing-superadmin')?.addEventListener('click', () => {
        currentView = 'SUPER_ADMIN';
        render();
      });

    } else if (currentView === 'SUPER_ADMIN') {
      // Small, Clean Super Admin Login View
      container.innerHTML = `
        <main class="ess-station-body" style="padding:40px 16px;">
          <div class="ess-pairing-box" style="max-width:400px;">
            <div class="ess-pairing-header">
              <div class="ess-brand-mark ess-brand-mark-lg" style="margin:0 auto 12px auto;background:#263238;">🔐</div>
              <div class="ess-pairing-title">ESS Platform Super Admin</div>
              <div class="ess-pairing-desc">
                Restricted platform cluster administration for Easy Smart System.
              </div>
            </div>

            <div id="ess-sa-error" class="ess-form-error" style="display:none;margin-bottom:14px;" role="alert"></div>

            <form id="ess-sa-form" class="ess-form" novalidate>
              <div class="ess-form-group">
                <label class="ess-label" for="sa-email">Super Admin Email *</label>
                <input
                  type="email"
                  id="sa-email"
                  class="ess-input"
                  placeholder="superadmin@ess.co.ke"
                  value="superadmin@ess.co.ke"
                  required
                />
              </div>

              <div class="ess-form-group">
                <label class="ess-label" for="sa-password">Master Password *</label>
                <input
                  type="password"
                  id="sa-password"
                  class="ess-input"
                  placeholder="Enter administrator password"
                  required
                />
              </div>

              <button type="submit" class="ess-btn ess-btn-primary ess-btn-block" id="ess-sa-submit" style="margin-top:8px;">
                Authenticate Super Admin
              </button>

              <button type="button" class="ess-btn ess-btn-secondary ess-btn-block" id="ess-sa-back" style="margin-top:8px;">
                &larr; Return to Station PIN Login
              </button>
            </form>
          </div>
        </main>
      `;

      const saForm = container.querySelector('#ess-sa-form') as HTMLFormElement | null;
      const saError = container.querySelector('#ess-sa-error') as HTMLElement | null;

      saForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = (container.querySelector('#sa-email') as HTMLInputElement).value.trim();
        const password = (container.querySelector('#sa-password') as HTMLInputElement).value;

        if (!email || !password) {
          if (saError) {
            saError.textContent = 'Please enter both administrator email and password.';
            saError.style.display = 'block';
          }
          return;
        }

        const submitBtn = container.querySelector('#ess-sa-submit') as HTMLButtonElement;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Authenticating...';
        if (saError) saError.style.display = 'none';

        try {
          await apiClient.login({ email, password });
          cleanupListeners();
          showToast({
            title: 'Super Admin Access Granted',
            message: 'Entering Platform Cluster Workspace',
            type: 'success',
          });
          onAuthenticated();
        } catch (err: any) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Authenticate Super Admin';
          if (saError) {
            saError.textContent = err?.message || 'Invalid administrator credentials.';
            saError.style.display = 'block';
          }
        }
      });

      container.querySelector('#ess-sa-back')?.addEventListener('click', () => {
        currentView = pairedData ? 'PAIRED_PIN' : 'PAIR_STATION';
        render();
      });
    }
  };

  render();
  return container;
}
